
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { adminService, GlobalStats, UserSummary } from '../services/adminService';
import { lockService } from '../services/lockService';
import { Shield, Users, Lock as LockIcon, Eye, Mail, Ban, Trash2, ArrowLeft, RefreshCw, MonitorPlay, Plus } from 'lucide-react';
import { Button } from './ui/Button';
import { LockType } from '../types';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [landingViews, setLandingViews] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<any>(null);

  const loadData = async () => {
      setLoading(true);
      try {
          const globalStats = await adminService.getGlobalStats();
          const userList = await adminService.getUsersList();
          setStats(globalStats);
          setUsers(userList);
          
          // Load system tracker
          const landingLock = await lockService.getLockBySlug('landing-page');
          setLandingViews(landingLock ? landingLock.viewsCount : null);

      } catch (e) {
          console.error(e);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      if (!user || !adminService.isAdmin(user.email)) {
          alert("Access Denied: Super Admin Only");
          navigate('/dashboard');
          return;
      }
      loadData();
    };
    checkAdmin();
  }, [navigate]);

  const handleBlock = async (userId: string) => {
      if(confirm(`Block user ${userId}? (Mock Action)`)) {
          await adminService.toggleBlockUser(userId);
          alert("User blocked signal sent (Requires Backend Function)");
      }
  };

  const handleDeleteUser = async (userId: string) => {
      const confirmText = prompt("Type 'NUKE' to delete all locks from this user.");
      if(confirmText === 'NUKE') {
          await adminService.deleteUserContent(userId);
          loadData(); // Refresh
      }
  };

  const initLandingTracker = async () => {
      if (!currentUser) return;
      try {
          await lockService.createLock(currentUser.id, {
              type: LockType.LINK,
              targetUrl: 'https://lockd.in',
              title: 'System: Landing Page',
              imageUrl: '',
              buttonText: '',
              // @ts-ignore - Passing custom slug for system
              slug: 'landing-page', 
              config: {}
          });
          loadData();
      } catch (e) {
          alert('Failed to init tracker: ' + e);
      }
  };

  if (loading) return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
          <div className="text-white flex items-center gap-2"><Shield className="animate-pulse" /> Verifying Clearance...</div>
      </div>
  );

  return (
    <div className="min-h-screen bg-slate-900 font-sans text-slate-100 pb-20">
      {/* Admin Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-700 rounded-full text-slate-400 hover:text-white">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Shield className="w-6 h-6 text-red-500" />
                        <h1 className="text-lg font-bold text-white tracking-wider">ADMIN DASHBOARD</h1>
                    </div>
                </div>
                <Button variant="secondary" onClick={loadData} className="bg-slate-800 text-white border-slate-600 hover:bg-slate-700 h-9 text-xs">
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh Data
                </Button>
            </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        
        {/* Global KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                <div className="flex items-center gap-3 mb-2 text-slate-400">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Active Creators</span>
                </div>
                <div className="text-4xl font-bold text-white">{stats?.totalUsers}</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                <div className="flex items-center gap-3 mb-2 text-slate-400">
                    <LockIcon className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Active Locks</span>
                </div>
                <div className="text-4xl font-bold text-white">{stats?.totalLocks}</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                <div className="flex items-center gap-3 mb-2 text-slate-400">
                    <Mail className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Leads Captured</span>
                </div>
                <div className="text-4xl font-bold text-emerald-400">{stats?.totalLeads}</div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700">
                <div className="flex items-center gap-3 mb-2 text-slate-400">
                    <Eye className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Lock Views</span>
                </div>
                <div className="text-4xl font-bold text-blue-400">{stats?.totalViews}</div>
            </div>
             {/* System Tracker Card */}
             <div className="bg-indigo-900/20 p-6 rounded-2xl border border-indigo-500/30">
                <div className="flex items-center gap-3 mb-2 text-indigo-300">
                    <MonitorPlay className="w-5 h-5" />
                    <span className="text-sm font-bold uppercase tracking-wider">Landing Hits</span>
                </div>
                {landingViews !== null ? (
                    <div className="text-4xl font-bold text-white">{landingViews}</div>
                ) : (
                    <button onClick={initLandingTracker} className="mt-1 text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-2 rounded-lg flex items-center">
                        <Plus className="w-3 h-3 mr-1" /> Enable Tracking
                    </button>
                )}
            </div>
        </div>

        {/* User Management Table */}
        <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" /> User Directory (Active Creators)
            </h2>
            <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-900/50 border-b border-slate-700 text-slate-400 text-xs uppercase tracking-wider">
                                <th className="p-4">User ID</th>
                                <th className="p-4">Last Active</th>
                                <th className="p-4 text-center">Locks</th>
                                <th className="p-4 text-center">Leads</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700 text-sm text-slate-300">
                            {users.map((user) => (
                                <tr key={user.userId} className="hover:bg-slate-700/50 transition-colors">
                                    <td className="p-4 font-mono text-xs text-slate-400">
                                        {user.userId}
                                    </td>
                                    <td className="p-4">
                                        {new Date(user.lastActive).toLocaleDateString()}
                                    </td>
                                    <td className="p-4 text-center font-bold text-white">
                                        {user.lockCount}
                                    </td>
                                    <td className="p-4 text-center font-bold text-emerald-400">
                                        {user.leadCount}
                                    </td>
                                    <td className="p-4 flex justify-end gap-2">
                                        <button 
                                            onClick={() => handleBlock(user.userId)}
                                            title="Block User"
                                            className="p-2 bg-slate-700 hover:bg-orange-900/50 hover:text-orange-500 rounded-lg transition-colors text-slate-400"
                                        >
                                            <Ban className="w-4 h-4" />
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteUser(user.userId)}
                                            title="Delete All Data"
                                            className="p-2 bg-slate-700 hover:bg-red-900/50 hover:text-red-500 rounded-lg transition-colors text-slate-400"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-slate-500">
                                        No users found via public lock data.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div className="p-4 bg-blue-900/20 border border-blue-800 rounded-xl text-blue-300 text-xs font-mono">
            <strong>SYSTEM NOTE:</strong> This dashboard aggregates data from the public 'locks' and 'leads' tables. 
            User IDs are derived from lock ownership. Blocking users requires Supabase Auth Admin API access (Backend).
        </div>

      </main>
    </div>
  );
};
